import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Row, Col, Form, Card, Button, Breadcrumb, Table, Pagination, Modal,
} from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAllLeaveCycles,
  getAllLeaveTypes,
  getAllDepartments,
  saveLeaveAllotments,
  getLeaveAllotmentsByCriteria,
  getAllEmployees,
  getAllLeaveTypeAllotments,
} from "../../../../api/onboarddeboard";
import { getAllRoles } from "../../../../api/roleAllocation";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - 2 + i);
const ITEMS_PER_PAGE = 20;

const LeaveAllotment = () => {
  const token = localStorage.getItem("token");

  const [showCreditModal, setShowCreditModal] = useState(false);

  const [actionType, setActionType] = useState("");
  const [staffType, setStaffType] = useState(""); // role id
  const [departmentId, setDepartmentId] = useState("");
  const [leaveCycleId, setLeaveCycleId] = useState("");
  const [year, setYear] = useState("");
  const [leaveNameId, setLeaveNameId] = useState("");
  const [errors, setErrors] = useState({});
  const [staffTypes, setStaffTypes] = useState([]); // roles
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [leaveCycles, setLeaveCycles] = useState([]);
  const [leaveNames, setLeaveNames] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [allotments, setAllotments] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [currentPage, setCurrentPage] = useState(1);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    getAllLeaveCycles(token).then((r) => setLeaveCycles(r.data || [])).catch(() => toast.error("Failed to load leave cycles."));
    getAllLeaveTypes(token).then((r) => setLeaveNames(r.data || [])).catch(() => toast.error("Failed to load leave names."));
    getAllDepartments(token).then((r) => setDepartments(r.data || [])).catch(() => toast.error("Failed to load departments."));
    getAllRoles(token)
  .then((r) => {
    const filteredRoles = (r.data || []).filter(
      (role) => !["SUPERADMIN", "USER"].includes(role.role)
    );

    setStaffTypes(filteredRoles);
  })
  .catch(() => toast.error("Failed to load staff types."));
  }, [token]);

 
  useEffect(() => {
    if (!leaveCycleId || !year) {
      setFromDate("");
      setToDate("");
      return;
    }
    const cycle = leaveCycles.find((c) => String(c.id) === String(leaveCycleId));
    if (!cycle) return;
    const toYear = cycle.toMonth < cycle.fromMonth ? Number(year) + 1 : Number(year);
    const lastDay = new Date(toYear, cycle.toMonth, 0).getDate();
    setFromDate(`${year}-${String(cycle.fromMonth).padStart(2, "0")}-01`);
    setToDate(`${toYear}-${String(cycle.toMonth).padStart(2, "0")}-${lastDay}`);
  }, [leaveCycleId, year, leaveCycles]);

  const fetchExistingAllotments = useCallback(
    async (freshList) => {
      if (!leaveCycleId || !year || !leaveNameId) return;
      try {
        // staffType is stored on the backend as the role NAME (see doSave), so it
        // must be resolved the same way here, otherwise the exact-match query
        // returns nothing and previously saved values never populate.
        const staffTypeName = staffTypes.find((s) => String(s.id) === String(staffType))?.role || staffType;
        const res = await getLeaveAllotmentsByCriteria(
          {
            leaveCycleId,
            year,
            leaveNameId,
            ...(staffTypeName && { staffType: staffTypeName }),
            ...(departmentId && { departmentId }),
          },
          token
        );
        const existing = res.data || [];

        const baseList = freshList || allotments;

        if (existing.length > 0 && baseList.length > 0) {
          const updated = baseList.map((row) => {
            // getLeaveAllotmentsByCriteria returns the raw LeaveAllotment entity, whose
            // embedded `employee` is a UserInfo record — its id field is `id`, not
            // `userInfoId` (that field only exists on the employees-list DTO).
            const found = existing.find((e) => e.employee?.id === row.employeeId);
            if (!found) return row;
            const safeAllocated = Number.isFinite(Number(found.allocatedDays)) ? Number(found.allocatedDays) : 0;
            const safeTotal = Number.isFinite(Number(found.totalBalance)) ? Number(found.totalBalance) : safeAllocated;
            return {
              ...row,
              openingBalance: safeAllocated,
              creditDays: 0,
              totalBalance: safeTotal,
              existingId: found.id,
              existingCredit: found.creditLeave || 0,
            };
          });
          setAllotments(updated);
          setSelectedIds(new Set());
        } else if (freshList) {
          setAllotments(freshList);
          setSelectedIds(new Set());
        }
      } catch {
        toast.error("Failed to fetch existing allotments.");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [token, leaveCycleId, year, leaveNameId, staffType, departmentId, staffTypes]
  );

  useEffect(() => {
    if (!actionType || !staffType || !leaveCycleId || !year || !leaveNameId) {
      setAllotments([]);
      setSelectedIds(new Set());
      return;
    }

    const loadData = async () => {
      setLoadingEmployees(true);
      try {
        let resolvedDefaultDays = 0;
        try {
          const typeRes = await getAllLeaveTypeAllotments(token);
          const list = typeRes.data || [];
          const match = list.find(
            (item) =>
              String(item.leaveType?.id) === String(leaveNameId) &&
              Number(item.role?.id) === Number(staffType) &&
              item.status !== false
          );
          resolvedDefaultDays = match ? match.totalLeaves || 0 : 0;
        } catch (err) {
          // non-fatal, default stays 0
        }

        const res = await getAllEmployees(token);
        let filtered = res.data || [];

        // Exclude inactive (deboarded) employees
        filtered = filtered.filter((u) => u.deboardEmp !== true);

        if (staffType) {
          filtered = filtered.filter((u) => Number(u.role?.id) === Number(staffType));
        }
        if (departmentId) {
          filtered = filtered.filter(
            (u) => String(u.department?.id ?? u.departmentId) === String(departmentId)
          );
        }

       const freshList = filtered.map((emp) => ({
  employeeId: emp.userInfoId,
  employeeName: emp.name || "—",
  departmentName: emp.department?.name ?? emp.departmentName ?? "—",
  openingBalance: actionType === "OPENING_BALANCE" ? resolvedDefaultDays : 0,
  creditDays: 0,
  totalBalance: 0,
  existingId: null,
  existingCredit: 0,
  maxAllotment: resolvedDefaultDays,
}));

        setCurrentPage(1);
        await fetchExistingAllotments(freshList);
      } catch (error) {
        toast.error("Failed to load employees.");
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionType, staffType, departmentId, leaveCycleId, year, leaveNameId, token]);

  const isAllSelected = allotments.length > 0 && selectedIds.size === allotments.length;

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allotments.map((a) => a.employeeId)));
  };

  const toggleSelect = (empId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(empId);
      const existing = [...prev].find((id) => String(id) === key);
      existing !== undefined ? next.delete(existing) : next.add(empId);
      return next;
    });
  };
const handleDaysChange = (employeeId, value) => {
  const isInvalid = value !== "" && !/^\d*$/.test(value);

  setErrors((p) => {
    const nextRowErrors = { ...(p.rowErrors || {}) };
    if (isInvalid) nextRowErrors[employeeId] = "Only numbers are allowed.";
    else delete nextRowErrors[employeeId];
    return { ...p, rowErrors: nextRowErrors };
  });

  setAllotments((prev) =>
    prev.map((row) => {
      if (String(row.employeeId) !== String(employeeId)) return row;
      if (isInvalid) {
        return { ...row, [actionType === "CREDIT_LEAVE" ? "creditDays" : "openingBalance"]: value };
      }
      if (actionType === "OPENING_BALANCE") {
        const v = value === "" ? "" : Number(value);
        return { ...row, openingBalance: v, totalBalance: Number(v || 0) + Number(row.existingCredit || 0) };
      }
      if (actionType === "CREDIT_LEAVE") {
        return { ...row, creditDays: value, totalBalance: Number(row.openingBalance || 0) + Number(value) };
      }
      return row;
    })
  );
};

  const validate = () => {
    const newErrors = {};
    if (!actionType) newErrors.actionType = "Action Type is required";
    if (!staffType) newErrors.staffType = "Staff Type is required";
    if (!leaveCycleId) newErrors.leaveCycleId = "Leave Cycle is required";
    if (!year) newErrors.year = "Year is required";
    if (!leaveNameId) newErrors.leaveNameId = "Leave Name is required";

    let tableError = "";
    if (selectedIds.size === 0) {
      tableError = "Please select at least one employee.";
    } else {
      const selected = allotments.filter((a) => selectedIds.has(a.employeeId));
      if (actionType === "OPENING_BALANCE") {
        const hasAny = selected.some((a) => Number(a.openingBalance) > 0);
        if (!hasAny) tableError = "Please enter opening balance for at least one selected employee.";
      } else if (actionType === "CREDIT_LEAVE") {
        const hasAny = selected.some((a) => Number(a.creditDays) > 0);
        if (!hasAny) tableError = "Please enter credit days for at least one selected employee.";
      }
    }
    if (tableError) toast.error(tableError);

    setErrors((prev) => ({ ...newErrors, rowErrors: prev.rowErrors }));
    return Object.keys(newErrors).length === 0 && !tableError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (actionType === "CREDIT_LEAVE") {
      setShowCreditModal(true);
      return;
    }
    await doSave();
  };

  const doSave = async () => {
    setShowCreditModal(false);
    const selectedRows = allotments.filter((a) => selectedIds.has(a.employeeId));

    setSubmitting(true);
    try {
      if (actionType === "OPENING_BALANCE") {
        const toSave = selectedRows.filter((r) => Number(r.openingBalance) > 0);
        if (toSave.length > 0) {
          await saveLeaveAllotments(
            {
              actionType,
              staffType: staffTypes.find((s) => String(s.id) === String(staffType))?.role || staffType,
              departmentId: departmentId ? Number(departmentId) : null,
              leaveCycleId: Number(leaveCycleId),
              year: Number(year),
              leaveNameId: Number(leaveNameId),
              allotments: toSave.map((r) => ({ employeeId: r.employeeId, allocatedDays: Number(r.openingBalance) })),
            },
            token
          );
        }
        toast.success(selectedRows.some((r) => r.existingId) ? "Opening balance updated successfully." : "Opening balance saved successfully.");
      } else {
        const toCredit = selectedRows.filter((r) => Number(r.creditDays) > 0);
        if (toCredit.length > 0) {
          await saveLeaveAllotments(
            {
              actionType,
              staffType: staffTypes.find((s) => String(s.id) === String(staffType))?.role || staffType,
              departmentId: departmentId ? Number(departmentId) : null,
              leaveCycleId: Number(leaveCycleId),
              year: Number(year),
              leaveNameId: Number(leaveNameId),
              allotments: toCredit.map((r) => ({ employeeId: r.employeeId, allocatedDays: Number(r.creditDays) })),
            },
            token
          );
        }
        toast.success("Credit leave applied successfully.");
      }

      await fetchExistingAllotments(allotments);
    } catch (error) {
      const data = error?.response?.data;
      const message = typeof data === "string" ? data : data?.message || data?.error || "Failed to save leave allotment.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setActionType("");
    setStaffType("");
    setDepartmentId("");
    setLeaveCycleId("");
    setYear("");
    setLeaveNameId("");
    setFromDate("");
    setToDate("");
    setAllotments([]);
    setSelectedIds(new Set());
    setErrors({});
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(allotments.length / ITEMS_PER_PAGE);
  const paginated = allotments.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  let lastColLabel = "Allocated Days";
  if (actionType === "OPENING_BALANCE") lastColLabel = "Opening Balance";
  else if (actionType === "CREDIT_LEAVE") lastColLabel = "Credit (Days)";

  const getRowInputValue = (row, hasRowError) => {
    let v;
    if (actionType === "OPENING_BALANCE") v = row.openingBalance;
    else if (actionType === "CREDIT_LEAVE") v = row.creditDays;
    else v = 0;
    if (v === "" || v === null || v === undefined) return v ?? "";
    if (hasRowError) return v;
    return Number.isFinite(Number(v)) ? v : "";
  };

  const hasAnyExistingSelected = allotments.some((a) => selectedIds.has(a.employeeId) && a.existingId);

  const disabledStyle = { backgroundColor: "#f8f9fa", cursor: "not-allowed" };

  let buttonText;
  if (submitting) {
    buttonText = (<><span className="spinner-border spinner-border-sm me-1" /> Saving...</>);
  } else if (actionType === "OPENING_BALANCE" && hasAnyExistingSelected) {
    buttonText = "Update";
  } else {
    buttonText = "Submit";
  }

  return (
    <Container fluid className="p-4">
      <style>{`
        .leave-toggle .form-check-input:checked { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
        .lc-breadcrumb a { text-decoration: none !important; color: #2E5CB8 !important; }
        .lc-breadcrumb .breadcrumb-item.active { color: #6c757d !important; }
        .pagination .page-item.active .page-link { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
      `}</style>
      <h4 className="page-main-heading" style={{ marginBottom: 12 }}>Leave Allotment</h4>
      <ToastContainer position="top-right" autoClose={4000} />
      <Breadcrumb className="lc-breadcrumb">
        <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
        <Breadcrumb.Item href="#">Leave Details</Breadcrumb.Item>
        <Breadcrumb.Item active>Leave Allotment</Breadcrumb.Item>
      </Breadcrumb>

      <Modal show={showCreditModal} onHide={() => setShowCreditModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #2E5CB8" }}>
          <Modal.Title style={{ color: "#2E5CB8", fontWeight: 700 }}>
            <i className="bi bi-info-circle me-2" />Confirm Credit Leave
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">The entered credit days will be added to each selected employee(s) opening balance. Do you want to continue?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreditModal(false)}>Cancel</Button>
          <Button variant="primary" style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }} onClick={doSave}>Continue</Button>
        </Modal.Footer>
      </Modal>

      <Row className="mb-4">
        <Col xl={12}>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">Action<span className="text-danger">*</span></Form.Label>
                      <Col sm={8}>
                        <Form.Select value={actionType} onChange={(e) => { setActionType(e.target.value); setErrors((p) => ({ ...p, actionType: "" })); }}>
                          <option value="">Select Action</option>
                          <option value="OPENING_BALANCE">Opening Balance</option>
                          <option value="CREDIT_LEAVE">Credit Leave</option>
                        </Form.Select>
                        {errors.actionType && <p style={{ color: "red", fontSize: "13px" }}>{errors.actionType}</p>}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">Staff<span className="text-danger">*</span></Form.Label>
                      <Col sm={8}>
                        <Form.Select value={staffType} onChange={(e) => { setStaffType(e.target.value); setErrors((p) => ({ ...p, staffType: "" })); }}>
                          <option value="">Select Staff Type</option>
                          {staffTypes.map((s) => (<option key={s.id} value={s.id}>{s.role}</option>))}
                        </Form.Select>
                        {errors.staffType && <p style={{ color: "red", fontSize: "13px" }}>{errors.staffType}</p>}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">Department</Form.Label>
                      <Col sm={8}>
                        <Form.Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                          <option value="">All Departments</option>
                          {departments.map((d) => (<option key={d.id} value={d.id}>{d.departmentName}</option>))}
                        </Form.Select>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">Leave Cycle<span className="text-danger">*</span></Form.Label>
                      <Col sm={8}>
                        <Form.Select
                          value={leaveCycleId}
                          onChange={(e) => {
                            setLeaveCycleId(e.target.value);
                            setYear("");
                            setLeaveNameId("");
                            setAllotments([]);
                            setSelectedIds(new Set());
                            setErrors((p) => ({ ...p, leaveCycleId: "" }));
                          }}
                        >
                          <option value="">Select Leave Cycle</option>
                          {leaveCycles.filter((c) => c.isActive === true).map((c) => (
    <option key={c.id} value={c.id}>{c.cycleName}</option>
))}
                        </Form.Select>
                        {errors.leaveCycleId && <p style={{ color: "red", fontSize: "13px" }}>{errors.leaveCycleId}</p>}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">Year<span className="text-danger">*</span></Form.Label>
                      <Col sm={8}>
                        <Form.Select
                          value={year}
                          onChange={(e) => {
                            setYear(e.target.value);
                            setLeaveNameId("");
                            setAllotments([]);
                            setSelectedIds(new Set());
                            setErrors((p) => ({ ...p, year: "" }));
                          }}
                        >
                          <option value="">Select Year</option>
                          {YEAR_OPTIONS.map((y) => (<option key={y} value={y}>{y}</option>))}
                        </Form.Select>
                        {errors.year && <p style={{ color: "red", fontSize: "13px" }}>{errors.year}</p>}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">Leave Name<span className="text-danger">*</span></Form.Label>
                      <Col sm={8}>
                        <Form.Select value={leaveNameId} onChange={(e) => { setLeaveNameId(e.target.value); setErrors((p) => ({ ...p, leaveNameId: "" })); }}>
                          <option value="">Select Leave Name</option>
                          {leaveNames.filter((l) => l.status === true).map((l) => (
    <option key={l.id} value={l.id}>{l.leaveName}</option>
))}
                        </Form.Select>
                        {errors.leaveNameId && <p style={{ color: "red", fontSize: "13px" }}>{errors.leaveNameId}</p>}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">From Date</Form.Label>
                      <Col sm={8}>
                        <Form.Control type="text" value={fromDate || "—"} readOnly style={disabledStyle} />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">To Date</Form.Label>
                      <Col sm={8}>
                        <Form.Control type="text" value={toDate || "—"} readOnly style={disabledStyle} />
                      </Col>
                    </Row>
                  </Col>

                  <Col>
                    {loadingEmployees && (
                      <div className="text-center py-3 text-muted">
                        <span className="spinner-border spinner-border-sm me-2" />Loading employees...
                      </div>
                    )}

                    {!loadingEmployees && allotments.length === 0 && actionType && staffType && (
                      <div className="text-center py-5 text-muted">
                        <i className="bi bi-person-x" style={{ fontSize: "2rem", opacity: 0.3 }} />
                        <p className="mt-2 mb-0" style={{ fontSize: "13px" }}>No employees found for selected criteria.</p>
                      </div>
                    )}

                    {!loadingEmployees && allotments.length > 0 && (
                      <Row className="mb-3">
                        <Col>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted">{selectedIds.size} of {allotments.length} employee(s) selected</small>
                          </div>

                          <Table bordered responsive>
                            <thead>
                              <tr>
                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "50px" }}>
                                  <Form.Check type="checkbox" checked={isAllSelected} onChange={toggleSelectAll} title="Select All" />
                                </th>
                                <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Sr.No</th>
                                <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>Employee Name</th>
                                <th style={{ backgroundColor: "#2E5CB8", color: "white" }}>{lastColLabel}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginated.map((row, index) => {
                                const isChecked = selectedIds.has(row.employeeId);
                                const hasRowError = Boolean(errors.rowErrors?.[row.employeeId]);
                                const inputValue = getRowInputValue(row, hasRowError);
                                return (
                                  <tr key={row.employeeId}>
                                    <td className="text-center align-middle">
                                      <Form.Check type="checkbox" checked={isChecked} onChange={() => toggleSelect(row.employeeId)} />
                                    </td>
                                    <td className="align-middle">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                    <td className="align-middle">{row.employeeName}</td>
                                    <td style={{ width: "180px" }} className="align-middle">
                                      <Form.Control
                                        type="text"
                                        value={inputValue}
                                        disabled={!isChecked}
                                        onChange={(e) => handleDaysChange(row.employeeId, e.target.value)}
                                        style={isChecked ? {} : disabledStyle}
                                      />
                                      {errors.rowErrors?.[row.employeeId] && (
                                        <p style={{ color: "red", fontSize: "12px", marginTop: 4, marginBottom: 0 }}>
                                          {errors.rowErrors[row.employeeId]}
                                        </p>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>

                          {totalPages > 1 && (
                            <div className="d-flex justify-content-end">
                              <Pagination>
                                <Pagination.Prev onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />
                                {[...Array(totalPages).keys()].map((n) => (
                                  <Pagination.Item key={n + 1} active={n + 1 === currentPage} onClick={() => setCurrentPage(n + 1)}>{n + 1}</Pagination.Item>
                                ))}
                                <Pagination.Next onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages} />
                              </Pagination>
                            </div>
                          )}
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>

                <Row className="align-items-center">
                  <Col className="mt-2 d-flex align-items-center justify-content-center gap-2">
                    <Button variant="primary" type="submit" disabled={submitting || allotments.length === 0} style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}>
                      {buttonText}
                    </Button>
                    <Button variant="secondary" type="button" onClick={resetForm}>Cancel</Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LeaveAllotment;